import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSalesReport, getSalesReportDetails, createSalesReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './SalesReport.module.css';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';

const SalesReport = ({ filterParams }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [expandedSaleId, setExpandedSaleId] = useState(null);
    const [saleDetails, setSaleDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const observer = useRef();
    const fetchSalesReportRef = useRef();

    // Get outlet name
    useEffect(() => {
        if (selectedOutlet && userAssignedOutlets?.length) {
            const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
            setOutletName(outlet?.name || 'Unknown');
        }
    }, [selectedOutlet, userAssignedOutlets]);

    const fetchSalesReport = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            // Get user_id from localStorage
            const userId = localStorage.getItem('user_id');
            if (!userId) {
                toast.error('User ID not found');
                return;
            }

            // Calculate dates using utility function
            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const params = {
                start_date: startDate,
                end_date: endDate,
                outlet: filterParams.outlet || selectedOutlet,
                user_id: userId,
                pagination: 1,
                page: page,
                archive: 1
            };

            // Add therapist filter if provided
            // Handle empty array (when all therapists are unselected)
            if (filterParams.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    // Format array as JSON string: [42,19,20]
                    params.therapist_id = JSON.stringify(filterParams.staff);
                } else {
                    // If it's a string or other format, wrap in array and stringify
                    params.therapist_id = JSON.stringify([filterParams.staff]);
                }
            }
            if (filterParams.archive === 1) {
                params.archive = 1;
            }

            if (isInitialLoad) {
                setPeriodText(getPeriodText(filterParams));
            }

            const response = await getSalesReport(params);

            if (response?.data?.success) {
                // API returns data in response.data.data.list.sales_report structure
                const dataList = response.data.data?.list?.sales_report || [];
                // Pagination info is in response.data.data.list.pagination
                const paginationInfo = response.data.data?.list?.pagination;

                if (isInitialLoad) {
                    setSalesData(dataList);
                } else {
                    setSalesData(prev => [...prev, ...dataList]);
                }

                setPagination(paginationInfo);

                // Check if there are more pages
                if (paginationInfo && paginationInfo.currentPage >= paginationInfo.lastPage) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            } else {
                if (isInitialLoad) {
                    const errorMessage = response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch sales report';
                    toast.error(errorMessage);
                    setSalesData([]);
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching    sales report:', error);
            if (isInitialLoad) {
                toast.error(error?.response?.data?.message || error?.message || 'An error occurred while fetching sales report');
                setSalesData([]);
            }
            setHasMore(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [filterParams, selectedOutlet]);

    // Store fetchSalesReport in ref for use in observer
    useEffect(() => {
        fetchSalesReportRef.current = fetchSalesReport;
    }, [fetchSalesReport]);

    // Trigger initial fetch when filterParams changes
    // Only call API if filterParams.outlet matches selectedOutlet (i.e., Generate was clicked)
    useEffect(() => {
        if (filterParams && filterParams.date && filterParams.outlet === selectedOutlet) {
            setCurrentPage(1);
            setSalesData([]);
            setHasMore(true);
            setExpandedSaleId(null);
            setSaleDetails({});
            setLoadingDetails({});
            fetchSalesReport(1, true);
        }
    }, [filterParams, fetchSalesReport, selectedOutlet]);

    // Intersection Observer for infinite scroll
    const lastItemRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && pagination) {
                const nextPage = currentPage + 1;
                if (nextPage <= pagination.lastPage && fetchSalesReportRef.current) {
                    setCurrentPage(nextPage);
                    fetchSalesReportRef.current(nextPage, false);
                }
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore, pagination, currentPage]);

    // Handle Order ID click - redirect to sales page with search
    const normalizeSaleId = (saleId) => {
        const cleanSaleId = String(saleId || '').replace('#', '').trim();
        return cleanSaleId || null;
    };

    const getSaleIdFromItem = (item) => {
        return normalizeSaleId(item?.['Order ID']);
    };

    const normalizeSalesDetails = (responseData) => {
        const payload = responseData?.data?.data || {};
        return Array.isArray(payload?.products) ? payload.products : [];
    };

    const fetchSaleDetails = async (saleId) => {
        if (!saleId) return;
        setLoadingDetails(prev => ({ ...prev, [saleId]: true }));

        try {
            const response = await getSalesReportDetails(saleId);
            const isSuccess = response?.data?.success ?? response?.success ?? true;
            if (!isSuccess) {
                throw new Error('Failed to fetch sale details');
            }

            const normalized = normalizeSalesDetails(response);
            setSaleDetails(prev => ({ ...prev, [saleId]: normalized }));
        } catch (error) {
            console.error('Error fetching sale details:', error);
            toast.error('Failed to fetch sale details');
        } finally {
            setLoadingDetails(prev => ({ ...prev, [saleId]: false }));
        }
    };

    const handleSaleExpand = async (item) => {
        const saleId = getSaleIdFromItem(item);
        if (!saleId) return;

        if (expandedSaleId === saleId) {
            setExpandedSaleId(null);
            return;
        }

        setExpandedSaleId(saleId);
        if (!saleDetails[saleId]) {
            await fetchSaleDetails(saleId);
        }
    };

    const formatCurrency = (value) => {
        const num = parseFloat(value || 0);
        return `$ ${num.toFixed(2)}`;
    };

    const handleSaleClick = (saleId) => {
        // Remove # character if present in sale ID
        const cleanSaleId = normalizeSaleId(saleId);
        if (!cleanSaleId) {
            return;
        }
        // Navigate to sales page with search parameter
        // navigate(`/sales?search=${cleanSaleId}&t=${Date.now()}`);
        let url = `/sales?search=${cleanSaleId}`;
        if (selectedOutlet) {
            url += `&outlet=${selectedOutlet}`;
        }
        window.open(url, "_blank");
    };

    const handleDownloadCSV = async () => {
        if (!salesData || salesData.length === 0) {
            toast.warning('No data available to download');
            return;
        }
        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }

        // Get user_id and token from localStorage
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');
        
        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        // Calculate dates using utility function
        const { startDate, endDate } = calculateReportDateRange(filterParams);

        // Create user-specific private channel name
        const channelName = `user.${userId}`;
        let channel;
        let detach = null;
        let toastId = null;

        setDownloadLoading(true);
        // Show persistent toast
        toastId = toast.loading("Downloading in progress...");
        
        try {
            // Join shared user channel — do not Echo.leave (payroll also uses user.{id}).
            const eventName = 'SalesReportReady';

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;
                        
                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }
                        
                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'sales-report.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            // Error handler
            if (typeof channel.error === 'function') {
                channel.error((error) => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            // Wait for subscription to be ready before calling API
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Call create API to trigger report generation with date parameters
            const downloadParams = {
                user_id: userId,
                start_date: startDate,
                end_date: endDate,
                outlet: filterParams.outlet || selectedOutlet
            };

            // Add therapist filter if provided
            // Handle empty array (when all therapists are unselected)
            if (filterParams.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    // Format array as JSON string: [42,19,20]
                    downloadParams.therapist_id = JSON.stringify(filterParams.staff);
                } else {
                    // If it's a string or other format, wrap in array and stringify
                    downloadParams.therapist_id = JSON.stringify([filterParams.staff]);
                }
            }
            if (filterParams.archive === 1) {
                downloadParams.archive = 1;
            }

            const createResponse = await createSalesReportDownload(downloadParams);

            // Check for success in the response
            const isSuccess = createResponse?.data?.success || createResponse?.success;
            
            if (isSuccess) {
                return; // Wait for socket event
            }

            // If not successful, handle error
            if (!isSuccess) {
                const backendError = createResponse?.data?.message || 
                                   createResponse?.message || 
                                   'Failed to initiate download';
                
                // Show user-friendly error message
                let userMessage = 'Failed to generate report. ';
                
                if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
                    userMessage += 'Server storage configuration issue. Please contact support.';
                } else if (backendError.includes('ACL')) {
                    userMessage += 'File permission error. Please contact support.';
                } else {
                    userMessage = backendError.substring(0, 200);
                }
                
                toast.error(userMessage, {
                    autoClose: 6000
                });
                
                cleanup();
                setDownloadLoading(false);
                return;
            }

        } catch (error) {
            const errorMessage = error?.response?.data?.message || 
                               error?.message || 
                               'An error occurred while preparing download';
            
            toast.error(errorMessage, {
                autoClose: 5000
            });
            
            if (toastId) toast.dismiss(toastId);
            setDownloadLoading(false);
            
            try {
                detach?.();
            } catch (e) {}
        }
    };

    return (
        <div className={styles.salesReportContainer}>
            {/* Header Section */}
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownloadCSV}
                downloadLoading={downloadLoading}
                hasData={salesData && salesData.length > 0}
                downloadButtonText="Download CSV"
            />

            {/* Table Section */}
            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : salesData && salesData.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.salesTable}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
                            <tr>
                                <th className={styles.colOrderId}>Sale ID</th>
                                <th className={styles.colCustomerName}>Customer Name</th>
                                <th className={styles.colQuantity}>Total Quantity</th>
                                <th className={styles.colTotal}>Total Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesData.map((item, index) => {
                                const isLastItem = index === salesData.length - 1;
                                const saleId = getSaleIdFromItem(item);
                                const isExpanded = saleId && expandedSaleId === saleId;
                                const isExpandable = Boolean(saleId);
                                const detailItems = saleDetails[saleId] || [];

                                return (
                                    <React.Fragment key={`${saleId || 'sale'}-${index}`}>
                                        <tr
                                            className={`${styles.saleRow} ${isExpanded ? styles.expanded : ''} ${!isExpandable ? styles.disabledRow : ''}`}
                                            ref={isLastItem ? lastItemRef : null}
                                            onClick={isExpandable ? () => handleSaleExpand(item) : undefined}
                                        >
                                            <td className={styles.colOrderId}>
                                                {item['Order ID'] ? (
                                                    <span 
                                                        className={styles.clickableOrderId}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleSaleClick(item['Order ID']);
                                                        }}
                                                        title="Click to view sale details"
                                                    >
                                                        {item['Order ID']}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td className={styles.colCustomerName}>{item['Customer Name'] || 'N/A'}</td>
                                            <td className={`${styles.colQuantity} ${styles.centerAlign}`}>
                                                {item['Quantity'] ?? 0}
                                            </td>
                                            <td className={`${styles.colTotal} ${styles.rightAlign}`}>
                                                {formatCurrency(item['Total'] ?? 0)}
                                            </td>
                                        </tr>

                                        {isExpanded && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan="4">
                                                    <div className={styles.detailsContainer}>
                                                        {loadingDetails[saleId] ? (
                                                            <div className={styles.loaderSmall}>
                                                                <Loader />
                                                            </div>
                                                        ) : detailItems.length > 0 ? (
                                                            <table className={styles.detailsItemsTable}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Product Name</th>
                                                                        <th>Quantity</th>
                                                                        <th>Unit Price</th>
                                                                        <th>Adjusted Unit Price</th>
                                                                        <th>Total Price</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {detailItems.map((detailItem, detailIndex) => {
                                                                        const productName = detailItem?.['Product Name'] || 'N/A';
                                                                        const quantity = detailItem?.['Quantity'] ?? 0;
                                                                        const unitPrice = detailItem?.['Unit Price'] ?? 0;
                                                                        const adjustedUnitPrice = detailItem?.['Adjusted Unit Price'] ?? unitPrice;
                                                                        const totalPrice = detailItem?.['Total Price'] ?? 0;

                                                                        return (
                                                                            <tr key={`${saleId || 'sale'}-${detailIndex}`}>
                                                                                <td>{productName}</td>
                                                                                <td>{quantity}</td>
                                                                                <td>{formatCurrency(unitPrice)}</td>
                                                                                <td>{formatCurrency(adjustedUnitPrice)}</td>
                                                                                <td>{formatCurrency(totalPrice)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <p className={styles.noDetails}>No sale details available</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
                        heading="No sales data found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default SalesReport;

